import React, { useCallback, useMemo, useRef, useState } from "react";
import { Image, Pressable, ScrollView, StyleSheet, TextInput, View, Platform } from "react-native";
import { Plus, File as FileIcon } from "lucide-react";
import { ConnectorType, StoragePathType } from "@axio/graphql/generated/graphql";
import { Cloud, Link as LinkIcon, Remove } from "../../../svgs/CustomCoursework";
import CustomText from "../CustomText/CustomText";
import { colors, radii, spacing } from "../../../../styles/design-system";
import INTEGRATIONS_ICONS from "../../../../assets/integrations";
import { ContentPickerModal } from "./ContentPickerModal";
import type {
  ExternalPickedItem,
  LinkPickedItem,
  LocalPickedItem,
  PickedItem,
  PickerContext,
} from "./ContentPicker.types";
import { CONNECTOR_META, useContentPickerSources } from "../../../../hooks/contentPicker/useContentPickerSources";
import Tooltip from "../../tooltip/Tooltip";
import { useTranslation } from "react-i18next";
import useFileUpload from "../../../../hooks/useFileUpload";
import { MIME_TO_EXTENSION_MAP } from "../../../file/FileUploadComponent/fileTypeHelper";

export interface ContentPickerProps {
  onFilesSelected?: (items: PickedItem[]) => void;
  maxFiles?: number;
  multiSelect?: boolean;
  storagePath?: StoragePathType;
  isSelectingList?: boolean;
  isPublic?: boolean;
  allowLinks?: boolean;
  uploadLinksAsFiles?: boolean;
  context?: PickerContext;
  allowedMimeTypes?: string[];
  allowedFileExtensions?: string[];
  uploadFormatsLabel?: string;
}

const EXTENSION_TO_MIME_TYPE = Object.entries(MIME_TO_EXTENSION_MAP).reduce<Record<string, string>>(
  (acc, [mimeType, extension]) => {
    if (!acc[extension]) {
      acc[extension] = mimeType;
    }
    return acc;
  },
  {}
);

const getExtensionFromFileName = (fileName: string): string => {
  const cleanName = fileName.split(/[?#]/)[0]?.toLowerCase().trim() ?? "";
  if (!cleanName.includes(".")) return "";
  return cleanName.split(".").pop() ?? "";
};

const getLinkFileName = (url: string, fallbackExtension: string): string => {
  let lastPathSegment = "";
  try {
    const parsedUrl = new URL(url);
    lastPathSegment = parsedUrl.pathname.split("/").filter(Boolean).pop() ?? "";
  } catch {
    lastPathSegment = url.split(/[?#]/)[0]?.split("/").filter(Boolean).pop() ?? "";
  }

  const decodedSegment = lastPathSegment ? decodeURIComponent(lastPathSegment) : "";
  if (decodedSegment && getExtensionFromFileName(decodedSegment)) {
    return decodedSegment;
  }

  return `linked-file.${fallbackExtension || "bin"}`;
};

function SourceTag({ item }: { item: PickedItem }) {
  const label =
    item.kind === "local"
      ? "content_picker_source_local"
      : item.kind === "link"
        ? (item as LinkPickedItem).url.replace(/^https?:\/\//, "").split("/")[0]
        : (CONNECTOR_META[(item as ExternalPickedItem).connectorType]?.label ?? (item as ExternalPickedItem).name);
  return (
    <View style={styles.tag}>
      <CustomText text={label} useTranslationText={item.kind === "local"} style={styles.tagText} />
    </View>
  );
}

export const ContentPicker: React.FC<ContentPickerProps> = ({
  onFilesSelected,
  maxFiles = 0,
  multiSelect = true,
  storagePath,
  isSelectingList = false,
  isPublic = false,
  allowLinks = true,
  uploadLinksAsFiles = false,
  context,
  allowedMimeTypes,
  allowedFileExtensions,
  uploadFormatsLabel,
}) => {
  const { t } = useTranslation();
  const { sources } = useContentPickerSources(context);
  const firstNotConnectedSource = sources.find((source) => source.status === "not_connected");
  const { handleServerUpload, handleServerUploadFromUrl } = useFileUpload({
    storagePath: storagePath as StoragePathType,
    isPublic,
  });
  const [items, setItems] = useState<PickedItem[]>([]);
  const [linkInput, setLinkInput] = useState("");
  const [openConnector, setOpenConnector] = useState<ConnectorType | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingState, setUploadingState] = useState<{ name: string; progress: number } | null>(null);
  const fileInputRef = useRef(null);
  const normalizedAllowedMimeTypes = useMemo(
    () => (allowedMimeTypes ?? []).map((mimeType) => mimeType.toLowerCase().trim()),
    [allowedMimeTypes]
  );
  const normalizedAllowedExtensions = useMemo(
    () => (allowedFileExtensions ?? []).map((extension) => extension.toLowerCase().trim().replace(/^\./, "")),
    [allowedFileExtensions]
  );
  const localFileAccept = useMemo(() => {
    const extensionTokens = normalizedAllowedExtensions.map((extension) => `.${extension}`);
    const tokens = [...normalizedAllowedMimeTypes, ...extensionTokens];
    return tokens.length > 0 ? tokens.join(",") : undefined;
  }, [normalizedAllowedExtensions, normalizedAllowedMimeTypes]);

  // ---------- helpers ----------

  const appendItems = useCallback(
    (next: PickedItem[]) => {
      setItems((prev) => {
        const combined = multiSelect ? [...prev, ...next] : next;
        const clamped = maxFiles > 0 ? combined.slice(0, maxFiles) : combined;
        onFilesSelected?.(clamped);
        return clamped;
      });
    },
    [maxFiles, multiSelect, onFilesSelected]
  );

  const isPickedFileAllowed = useCallback(
    ({ name, mimeType }: { name: string; mimeType?: string }): boolean => {
      if (normalizedAllowedMimeTypes.length === 0 && normalizedAllowedExtensions.length === 0) {
        return true;
      }

      const normalizedMimeType = mimeType?.toLowerCase().trim();
      if (normalizedMimeType && normalizedAllowedMimeTypes.includes(normalizedMimeType)) {
        return true;
      }

      const extension = getExtensionFromFileName(name);
      if (extension && normalizedAllowedExtensions.includes(extension)) {
        return true;
      }

      return false;
    },
    [normalizedAllowedExtensions, normalizedAllowedMimeTypes]
  );

  const isLocalFileAllowed = useCallback(
    (file: File): boolean => isPickedFileAllowed({ name: file.name, mimeType: file.type }),
    [isPickedFileAllowed]
  );

  // ---------- file upload ----------

  const performUpload = useCallback(
    async (files: File[]) => {
      const supportedFiles = files.filter(isLocalFileAllowed);
      if (!supportedFiles.length) return;

      const picked: LocalPickedItem[] = [];

      for (const file of supportedFiles) {
        setUploadingState({ name: file.name, progress: 0 });

        if (storagePath) {
          const reader = new FileReader();
          await new Promise<void>((resolve) => {
            reader.onloadend = async () => {
              try {
                const uri = reader.result as string;
                const result = await handleServerUpload({
                  uri,
                  mimeType: file.type,
                  size: file.size,
                  name: file.name,
                  onProgress: (progress) => {
                    setUploadingState({ name: file.name, progress });
                  },
                });

                if (result) {
                  setUploadingState({ name: file.name, progress: 100 });
                  picked.push({
                    kind: "local",
                    file: file,
                    name: file.name,
                    mimeType: file.type || undefined,
                    sizeBytes: file.size,
                    uploadedFileId: result.file.id,
                    url: result.file.fileURL,
                  });
                }
                setTimeout(() => resolve(), 300);
              } catch (e) {
                console.error("Upload error", e);
                resolve();
              }
            };
            reader.onerror = () => resolve();
            reader.readAsDataURL(file);
          });
        } else {
          picked.push({
            kind: "local",
            file,
            name: file.name,
            mimeType: file.type || undefined,
            sizeBytes: file.size,
          });
        }
      }

      if (picked.length > 0) {
        appendItems(picked);
      }
      setUploadingState(null);
    },
    [appendItems, handleServerUpload, isLocalFileAllowed, storagePath]
  );

  // ---------- local upload ----------

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;
    performUpload(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---------- link ----------

  const uploadLinkAsFile = useCallback(
    async (url: string) => {
      setUploadingState({ name: url, progress: 0 });

      try {
        const urlExtension = getExtensionFromFileName(url);
        const extensionMimeType = EXTENSION_TO_MIME_TYPE[urlExtension] || "";
        const fallbackExtension = MIME_TO_EXTENSION_MAP[extensionMimeType] ?? urlExtension;
        const fileName = urlExtension ? getLinkFileName(url, fallbackExtension) : undefined;

        if (fileName && !isPickedFileAllowed({ name: fileName, mimeType: extensionMimeType })) {
          return;
        }

        const result = await handleServerUploadFromUrl({
          url,
          name: fileName,
        });

        if (!result) return;

        const uploadedFileName = result.file.fileName ?? fileName ?? url;
        const uploadedMimeType = EXTENSION_TO_MIME_TYPE[getExtensionFromFileName(uploadedFileName)];
        if (!isPickedFileAllowed({ name: uploadedFileName, mimeType: uploadedMimeType })) {
          return;
        }

        appendItems([
          {
            kind: "local",
            name: uploadedFileName,
            mimeType: uploadedMimeType || undefined,
            uploadedFileId: result.file.id,
            url: result.file.fileURL,
          },
        ]);
        setLinkInput("");
      } catch (e) {
        console.error("Link upload error", e);
      } finally {
        setUploadingState(null);
      }
    },
    [appendItems, handleServerUploadFromUrl, isPickedFileAllowed]
  );

  const handleAddLink = useCallback(() => {
    if (!allowLinks) return;
    const trimmed = linkInput.trim();
    if (!trimmed) return;
    if (uploadLinksAsFiles && storagePath) {
      void uploadLinkAsFile(trimmed);
      return;
    }
    const picked: LinkPickedItem = { kind: "link", url: trimmed, name: trimmed };
    appendItems([picked]);
    setLinkInput("");
  }, [allowLinks, appendItems, linkInput, storagePath, uploadLinkAsFile, uploadLinksAsFiles]);

  // ---------- external import ----------

  const handleExternalImport = useCallback(
    (externalItems: ExternalPickedItem[]) => {
      appendItems(externalItems);
      setOpenConnector(null);
    },
    [appendItems]
  );

  const handleRemoveItem = useCallback(
    (indexToRemove: number) => {
      setItems((prev) => {
        const next = prev.filter((_, index) => index !== indexToRemove);
        onFilesSelected?.(next);
        return next;
      });
    },
    [onFilesSelected]
  );

  // ---------- drag and drop  ----------

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files ?? []);
      if (!files.length) return;

      performUpload(files as any);
    },
    [performUpload]
  );

  const DropzoneWrapper = Platform.OS === "web" ? "div" : View;
  const dropzoneProps =
    Platform.OS === "web"
      ? {
          onDragEnter: handleDragEnter,
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop,
          style: {
            ...styles.pickerBox,
            ...(isDragging ? styles.pickerBoxDragging : {}),
            display: "flex",
            flexDirection: "column",
          } as React.CSSProperties,
        }
      : {
          style: [styles.pickerBox, isDragging && styles.pickerBoxDragging],
        };

  return (
    <View style={styles.mainContainer}>
      <View style={styles.container}>
        <DropzoneWrapper {...(dropzoneProps as any)}>
          {Platform.OS === "web" ? (
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple={multiSelect && maxFiles !== 1}
              accept={localFileAccept}
              onChange={handleFileChange as any}
              aria-label={t("text.content_picker_upload_aria")}
            />
          ) : null}

          {/* manual upload or Dropzone */}
          {uploadingState ? (
            <View style={styles.uploadingContainer}>
              <View style={styles.uploadingIconCircle}>
                <FileIcon size={20} color={colors.brand[500]} />
              </View>
              <CustomText text={uploadingState.name} useTranslationText={false} style={styles.uploadingFileName} />
              <CustomText text="content_picker_modal_loading" useTranslationText={true} style={styles.uploadingText} />
              <View style={styles.progressBarBg}>
                <View style={[styles.progressBarFill, { width: `${uploadingState.progress}%` }]} />
              </View>
            </View>
          ) : (
            <Pressable
              style={styles.dropzone}
              onPress={() => fileInputRef.current?.click()}
              accessibilityRole="button"
              accessibilityLabel={t("text.content_picker_upload_cta")}>
              <View style={styles.uploadIconCircle}>
                <Cloud width={24} height={24} color={colors.grey[700]} />
              </View>
              <View style={styles.dropzoneText}>
                <View style={styles.uploadCtaRow}>
                  <CustomText
                    text={isDragging ? "content_picker_drag_active" : "content_picker_upload_cta"}
                    useTranslationText={true}
                    size="s"
                    style={styles.uploadCtaBlue}
                  />
                  {!isDragging && (
                    <CustomText
                      text={"content_picker_upload_or_drag_drop"}
                      useTranslationText={true}
                      size="s"
                      style={styles.uploadCtaDark}
                    />
                  )}
                </View>
                {!isDragging && (
                  <CustomText
                    text={uploadFormatsLabel ?? "content_picker_upload_formats"}
                    useTranslationText={uploadFormatsLabel == null}
                    size="xs"
                    style={styles.uploadFormats}
                  />
                )}
              </View>
            </Pressable>
          )}

          <View style={styles.dashedSeparator} />

          {/* Link input */}
          {allowLinks && (
            <>
              <View style={styles.linkRow}>
                <LinkIcon width={16} height={16} color={colors.grey[500]} />
                <TextInput
                  placeholder={t("text.content_picker_link_placeholder")}
                  placeholderTextColor={colors.grey[500]}
                  value={linkInput}
                  onChangeText={setLinkInput}
                  onSubmitEditing={handleAddLink}
                  returnKeyType="done"
                  style={styles.linkInput}
                  accessibilityLabel={t("text.content_picker_link_placeholder")}
                  // @ts-expect-error outlineStyle is valid on RN Web
                  outlineStyle="none"
                />
              </View>

              <View style={styles.dashedSeparator} />
            </>
          )}
          {/* Quick connector  */}

          {sources.length > 0 && (
            <View style={styles.importStrip}>
              <CustomText
                text={"content_picker_import_label_with_colon"}
                useTranslationText={true}
                size="xs"
                style={styles.importLabel}
              />
              <ScrollView
                horizontal={true}
                style={styles.iconRowScroll}
                contentContainerStyle={styles.iconRow}
                showsHorizontalScrollIndicator={false}>
                {sources.map((src) => {
                  const iconUri = INTEGRATIONS_ICONS[src.iconKey as keyof typeof INTEGRATIONS_ICONS];
                  return (
                    <Tooltip
                      key={src.connectorType}
                      content={
                        src.status === "not_connected"
                          ? t("text.content_picker_connect_source", { source: src.label })
                          : src.label
                      }
                      placement="top"
                      showArrow={true}>
                      <View style={styles.iconWrapper}>
                        <Pressable
                          style={({ hovered }) => [styles.iconBtn, hovered && styles.iconBtnHover]}
                          onPress={() => setOpenConnector(src.connectorType)}
                          accessibilityRole="button"
                          accessibilityLabel={t("text.content_picker_import_from_source", { source: src.label })}>
                          {iconUri ? (
                            <Image source={{ uri: iconUri }} style={styles.iconImg} resizeMode="contain" />
                          ) : (
                            <View style={styles.iconFallback} />
                          )}
                        </Pressable>
                        {src.status === "not_connected" && (
                          <View style={styles.notConnectedBadge}>
                            <LinkIcon width={10} height={10} color={colors.grey[500]} />
                          </View>
                        )}
                      </View>
                    </Tooltip>
                  );
                })}
                {firstNotConnectedSource ? (
                  <Tooltip
                    key="connect-a-service"
                    content={t("text.content_picker_connect_a_service")}
                    placement="top"
                    showArrow={true}>
                    <Pressable
                      style={styles.iconBtn}
                      accessibilityRole="button"
                      accessibilityLabel={t("text.content_picker_connect_a_service")}
                      onPress={() => setOpenConnector(firstNotConnectedSource.connectorType)}>
                      <Plus size={16} color={colors.grey[700]} />
                    </Pressable>
                  </Tooltip>
                ) : null}
              </ScrollView>
            </View>
          )}
        </DropzoneWrapper>
      </View>

      {/* ── Selection list ── */}
      {isSelectingList && items.length > 0 && (
        <View style={styles.selectionList}>
          <CustomText
            text={`content_picker_selected_items_${items.length === 1 ? "one" : "other"}`}
            interpolation={{ count: items.length }}
            useTranslationText={true}
            size="s"
            style={styles.selectionHeader}
          />
          {items.map((item, i) => (
            <View key={`${item.kind}-${i}`} style={styles.selectionRow}>
              <CustomText
                text={item.name}
                useTranslationText={false}
                size="xs"
                style={styles.selectionName}
                numberOfLines={1}
              />
              <SourceTag item={item} />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("text.content_picker_remove_item", { name: item.name })}
                onPress={() => handleRemoveItem(i)}
                style={styles.selectionRemoveButton}>
                <Remove width={14} height={14} color={colors.grey[600]} />
              </Pressable>
            </View>
          ))}
        </View>
      )}

      {/* ── External browser modal ── */}
      <ContentPickerModal
        visible={!!openConnector}
        onClose={() => setOpenConnector(null)}
        initialConnectorType={openConnector}
        multiSelect={multiSelect}
        onImport={handleExternalImport}
        context={context}
        allowedMimeTypes={allowedMimeTypes}
        allowedFileExtensions={allowedFileExtensions}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    padding: spacing.lg,
    borderRadius: radii.xl,
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[100],
    width: "100%",
  },
  container: {
    width: "100%",
  },

  pickerBox: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.lg,
    borderStyle: "dashed",
    overflow: "hidden",
  },
  pickerBoxDragging: {
    borderColor: colors.brand[400],
    backgroundColor: colors.brand[25],
  },
  dashedSeparator: {
    height: 1,
    width: "100%",
    borderTopWidth: 1,
    borderColor: colors.grey[300],
    borderStyle: "dashed",
  },
  dropzone: {
    paddingVertical: 48,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  uploadIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.grey[50],
    alignItems: "center",
    justifyContent: "center",
  },
  dropzoneText: {
    alignItems: "center",
    gap: 8,
  },
  uploadCtaRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  uploadCtaBlue: {
    color: colors.brand[600],
  },
  uploadCtaDark: {
    color: colors.grey[700],
  },
  uploadFormats: {
    color: colors.grey[500],
  },
  uploadingContainer: {
    paddingVertical: 48,
    alignItems: "center",
    justifyContent: "center",
  },
  uploadingIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.brand[100],
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  uploadingFileName: {
    fontSize: 14,
    color: colors.grey[900],
    fontWeight: "500",
    marginBottom: 4,
  },
  uploadingText: {
    fontSize: 12,
    color: colors.grey[500],
    marginBottom: 16,
  },
  progressBarBg: {
    width: "80%",
    height: 6,
    backgroundColor: colors.grey[100],
    borderRadius: 3,
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    backgroundColor: colors.brand[500],
    borderRadius: 3,
  },
  linkRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 16,
    gap: 12,
  },
  linkInput: {
    flex: 1,
    fontSize: 14,
    color: colors.grey[900],
  } as any,
  importStrip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 16,
    gap: 12,
  },
  importLabel: {
    color: colors.grey[600],
  },
  iconRowScroll: {
    flex: 1,
    minWidth: 0,
  },
  iconRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingRight: 4,
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  iconBtnHover: {
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  iconImg: {
    width: 20,
    height: 20,
  },
  iconFallback: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.grey[200],
  },
  iconWrapper: {
    position: "relative",
  },
  notConnectedBadge: {
    position: "absolute",
    bottom: -4,
    right: -4,
    backgroundColor: colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    width: 16,
    height: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  // Selection list
  selectionList: {
    gap: 8,
    marginTop: 24,
    width: "100%",
  },
  selectionHeader: {
    color: colors.grey[700],
    marginBottom: 8,
  },
  selectionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    gap: 12,
  },
  selectionName: {
    flex: 1,
    color: colors.grey[600],
  },
  selectionRemoveButton: {
    padding: 4,
    marginLeft: 4,
  },

  // tag
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: colors.grey[200],
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.grey[200],
    alignSelf: "flex-start",
  },
  tagText: {
    fontSize: 10,
    color: colors.grey[600],
  },
});
